import { Box, IconButton, Stack, Typography } from '@mui/material';
import {
  BaseUserSchoolRelation,
  GetReportRecipientsQueryFilters,
  ReportForAssessment,
  useGetReportRecipientsQuery,
} from '@schooly/api';
import { AvatarAuth } from '@schooly/components/avatar-auth';
import { FilterButton, SearchInput } from '@schooly/components/filters';
import { useFlag } from '@schooly/hooks/use-flag';
import { useInfiniteScroll } from '@schooly/hooks/use-infinite-scroll';
import { CrossIcon, FilterIcon, Loading } from '@schooly/style';
import { getUserFullName } from '@schooly/utils/user-helpers';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { CollapsiblePanel } from '../../../components/common/CollapsiblePanel';
import { ModalNavigation } from '../../../components/common/ModalNavigation';
import { NoSearchResultsFound } from '../../../components/common/NoSearchResultsFound/NoSearchResultsFound';
import { useCollapsiblePanel } from '../../../context/collapsiblePanel/useCollapsiblePanel';
import useAppLocation from '../../../hooks/useAppLocation';
import useQueryStringParams from '../../../hooks/useQueryStringParams';
import { InlineFiltersTags } from './InlineFiltersTags';
import {
  ReportsPreviewContentStyled,
  ReportsPreviewSideBar,
  ReportsPreviewSideBarTab,
} from './ReportsPreviewContent.styled';
import { ReportsPreviewEntries } from './ReportsPreviewEntries/ReportsPreviewEntries';
import { ReportPreviewModalFiltersPopup } from './ReportsPrviewModalFiltersPopup';
type ReportsPreviewContentProps = {
  report: ReportForAssessment;
};

const DEFAULT_FILTERS: GetReportRecipientsQueryFilters = {};

export const ReportsPreviewContent: FC<ReportsPreviewContentProps> = ({ report }) => {
  const { id } = useParams<'id'>();
  const { recipient_id: recipientId } = useQueryStringParams();
  const navigate = useNavigate();
  const location = useAppLocation();
  const { formatMessage } = useIntl();
  const [query, setQuery] = useState('');
  const { state } = useCollapsiblePanel();
  const [filtersModalVisible, openFilterModal, closeFiltersModal] = useFlag(false);

  const [filters, setFilters] = useState<GetReportRecipientsQueryFilters>(DEFAULT_FILTERS);

  const {
    data: requestData,
    hasNextPage,
    fetchNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
  } = useGetReportRecipientsQuery(
    {
      report_id: id ?? '',
      query,
      filters,
    },
    { enabled: Boolean(id), refetchOnMount: 'always' },
  );

  const loaderRef = useInfiniteScroll(isFetching, fetchNextPage, hasNextPage);

  const displayedList = useMemo<BaseUserSchoolRelation[]>(() => {
    return (
      requestData?.pages.reduce<BaseUserSchoolRelation[]>(
        (prev, curr) => [...prev, ...curr.results],
        [],
      ) ?? []
    );
  }, [requestData]);

  const selectedRecipient = useMemo<BaseUserSchoolRelation | undefined>(() => {
    if (!displayedList?.length || !recipientId) {
      return undefined;
    }

    return displayedList.find((r) => r.relation_id === recipientId);
  }, [displayedList, recipientId]);

  const handleRecipientClick = useCallback(
    (relationId: string) => {
      navigate(
        { pathname: `/reports/${id}`, search: `?recipient_id=${relationId}` },
        { state: location.state },
      );
    },
    [id, location.state, navigate],
  );

  const firstStudentRelationId = displayedList[0]?.relation_id;

  useEffect(() => {
    if (!!recipientId) return;
    if (!firstStudentRelationId) return;

    handleRecipientClick(firstStudentRelationId);
  }, [firstStudentRelationId, handleRecipientClick, recipientId]);

  const showEmptyStub = !isLoading && !displayedList.length;

  const renderList = () => {
    if (isLoading) {
      return <Loading />;
    }

    if (showEmptyStub) {
      return <NoSearchResultsFound type="small" />;
    }

    return (
      <>
        {displayedList.map((recipient) => (
          <ReportsPreviewSideBarTab
            active={recipient.relation_id === recipientId}
            id={`student-${recipient.relation_id}`}
            key={recipient.relation_id}
            onClick={() => handleRecipientClick(recipient.relation_id)}
          >
            <Stack direction="row" alignItems="center" gap={1}>
              <AvatarAuth user={recipient} withAvatarPreview />
              <Typography variant="h3" color="common.grey2">
                {getUserFullName(recipient)}
              </Typography>
            </Stack>
          </ReportsPreviewSideBarTab>
        ))}

        {hasNextPage && (
          <Box py={3}>
            {isFetchingNextPage && <Loading />}
            <div ref={loaderRef} />
          </Box>
        )}
      </>
    );
  };

  const isPanelOpen = state.isOpen;

  const hasFiltersApplied = useMemo(() => {
    return Object.values(filters).some((filter) => filter?.length);
  }, [filters]);

  return (
    <>
      <ReportsPreviewContentStyled>
        <CollapsiblePanel width={300}>
          <Stack
            sx={(theme) => ({
              flexGrow: 0,
              px: 2,
              pb: 1,
              pt: 2,
              gap: 1,
              borderRight: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.common.white,
            })}
          >
            <SearchInput
              placeholder={formatMessage(
                { id: 'assessments-SearchAmongCount' },
                {
                  assessmentsPlural: formatMessage({ id: 'assessments-Groups' }).toLowerCase(),
                },
              )}
              onChangeText={setQuery}
              value={query}
              canClear={false}
              InputProps={{
                endAdornment: (
                  <Stack pl={0.75} gap={0.5} pr={1} flexDirection="row">
                    {!!query && (
                      <IconButton
                        sx={(theme) => ({ '&:hover': { color: theme.palette.text.primary } })}
                        inverse
                        onClick={() => setQuery('')}
                      >
                        <CrossIcon />
                      </IconButton>
                    )}
                    <FilterButton onClick={openFilterModal} hasFiltersApplied={hasFiltersApplied}>
                      <FilterIcon />
                    </FilterButton>
                  </Stack>
                ),
              }}
            />
            <InlineFiltersTags
              filters={filters}
              onOpenFilters={openFilterModal}
              onResetFilters={() => setFilters({})}
            />
          </Stack>
          <ReportsPreviewSideBar>
            <Box sx={{ flex: '1 1 auto', px: 2, pb: 10 }}>{renderList()}</Box>
          </ReportsPreviewSideBar>
        </CollapsiblePanel>

        <ReportsPreviewEntries
          report={report}
          recipientId={recipientId}
          showEmptyStub={!recipientId && showEmptyStub}
          fullView={!isPanelOpen}
        />
      </ReportsPreviewContentStyled>
      {!!selectedRecipient && !isPanelOpen && (
        <ModalNavigation
          selectedItem={selectedRecipient}
          items={displayedList}
          onClick={(r) => {
            handleRecipientClick(r.relation_id);
          }}
          getItemLabel={(r) => getUserFullName(r)}
        />
      )}
      {filtersModalVisible && (
        <ReportPreviewModalFiltersPopup
          report={report}
          filters={filters}
          defaultFilters={DEFAULT_FILTERS}
          onClose={closeFiltersModal}
          onSetFilters={setFilters}
        />
      )}
    </>
  );
};
